// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Cursor / scrub readout renderer: builds and updates the marker-detail tooltip
// that shows engine values at the hovered or scrubbed point on the flight and
// explorer charts.
//
// - updateScrubReadout: the single public entry point. Routes a point to the
//   right scope's detail container and either updates live (throttled via
//   scheduleLiveScrubReadout) or immediately.
// - markerMetric* / markerEngineProbeInfo / formatMetric: pure markup + label
//   helpers that turn a point + engine descriptors into grouped metric HTML.
// - updateMarkerDetail / updateLiveMarkerDetailText: signature-guarded DOM writes.
//
// Marker-only state (id counter, the point->id WeakMap, and the live-readout
// throttle state) lives in this module because nothing else touches it. Shared
// app state (selected flight/cylinder/health key, detection sensitivity, active
// series) is injected via getters so it stays in sync with app.js.

import { colors, seriesMeta } from "../core/config.js";
import { engineScopedChannelLabel } from "../core/engines.js";
import { escapeHtml } from "../core/html.js";
import { formatElapsedTime } from "../core/format.js";
import { isTitMetricKey, isTurboMetricKey, metricDisplayFor } from "../charts/presentation.js";
import { pointInDetectionWindow } from "../findings/detection-windows.js";

export function createMarkerReadoutRenderer({
  currentEngineDescriptors,
  formatMarkerPointTime,
  selectedDetectionWindows,
  setText,
  titleCase,
  getActiveSeries,
  getSelectedCylinderNumber,
  getSelectedFlight,
  getSourceName,
  getDetectionSensitivity,
  getSelectedHealthKey,
  getSelectedOperatingContextKey,
}) {
  // Marker-only state (moved out of app.js; nothing else references it).
  let markerPointIdCounter = 0;
  const markerPointIds = new WeakMap();
  const liveScrubReadoutMinIntervalMs = 45;
  const explorerLiveScrubReadoutDelayMs = 60;
  const liveScrubReadoutState = {
    flight: { lastUpdate: 0, pendingPoint: null, timer: null },
    explorer: { lastUpdate: 0, pendingPoint: null, timer: null },
  };

function updateScrubReadout(point, options = {}) {
  const scope = options.scope ?? "all";
  if (options.live) {
    if (scope === "flight") {
      scheduleLiveScrubReadout("flight", point);
      return;
    }
    if (scope === "explorer") {
      scheduleLiveScrubReadout("explorer", point);
      return;
    }
  }
  if (scope === "all" || scope === "flight") {
    updateMarkerDetail(document.querySelector("#markerDetail"), point);
  }
  if (scope === "all" || scope === "explorer") {
    updateMarkerDetail(document.querySelector("#explorerMarkerDetail"), point);
  }
}

function markerDetailContainerForScope(scope) {
  return document.querySelector(scope === "explorer" ? "#explorerMarkerDetail" : "#markerDetail");
}

function scheduleLiveScrubReadout(scope, point) {
  const state = liveScrubReadoutState[scope];
  const container = markerDetailContainerForScope(scope);
  if (!state || !container || !point) return;
  state.pendingPoint = point;
  const commit = () => {
    const nextPoint = state.pendingPoint;
    state.pendingPoint = null;
    state.timer = null;
    state.lastUpdate = performance.now();
    updateMarkerDetail(container, nextPoint);
  };
  if (scope === "explorer") {
    updateLiveMarkerDetailText(container, point);
    if (state.timer) clearTimeout(state.timer);
    state.timer = window.setTimeout(commit, explorerLiveScrubReadoutDelayMs);
    return;
  }
  const now = performance.now();
  const elapsed = now - state.lastUpdate;
  if (elapsed >= liveScrubReadoutMinIntervalMs) {
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }
    commit();
    return;
  }
  if (!state.timer) {
    state.timer = window.setTimeout(commit, Math.max(0, liveScrubReadoutMinIntervalMs - elapsed));
  }
}

function markerPointId(point) {
  if (!point || typeof point !== "object") return "";
  if (!markerPointIds.has(point)) {
    markerPointIdCounter += 1;
    markerPointIds.set(point, markerPointIdCounter);
  }
  return markerPointIds.get(point);
}

function formatMetric(label, value, suffix, digits) {
  if (!Number.isFinite(value)) return "";
  return `<span>${label} ${Number(value).toFixed(digits)}${suffix}</span>`;
}

function markerMetricLabel(key, metric = null) {
  return engineScopedChannelLabel(key, currentEngineDescriptors()) ?? metric?.label ?? seriesMeta[key]?.label ?? titleCase(key);
}

function markerEngineProbeInfo(key) {
  const engines = currentEngineDescriptors();
  for (const engine of engines) {
    for (const cylinder of engine.cylinders ?? []) {
      if (key === cylinder.egtKey) {
        return {
          group: `${engine.shortLabel} EGT`,
          itemLabel: String(cylinder.number),
        };
      }
      if (key === cylinder.chtKey) {
        return {
          group: `${engine.shortLabel} CHT`,
          itemLabel: String(cylinder.number),
        };
      }
    }
  }
  return null;
}

// Canonical display order for the readout. `metricKeys` arrives in the order
// the user toggled channels on, so turning cylinders on out of sequence printed
// them out of sequence ("1 2 3 6 5 4"). Rank instead against the engine
// descriptors, which already hold engines in order and each engine's cylinders
// in cylinder-number order, so the readout reads the same however it was built.
//
// Ranks are compared component-wise: probes before everything else, then engine,
// then EGT before CHT, then cylinder position. Non-probe channels fall back to
// their position in seriesMeta, which declares them in a sensible order.
function markerMetricRankMap() {
  const ranks = new Map();
  currentEngineDescriptors().forEach((engine, engineIndex) => {
    (engine.cylinders ?? []).forEach((cylinder, position) => {
      if (cylinder?.egtKey) ranks.set(cylinder.egtKey, [0, engineIndex, 0, position]);
      if (cylinder?.chtKey) ranks.set(cylinder.chtKey, [0, engineIndex, 1, position]);
    });
  });
  return ranks;
}

function orderMarkerMetricKeys(metricKeys) {
  const ranks = markerMetricRankMap();
  const seriesOrder = Object.keys(seriesMeta);
  const rankFor = (key) => {
    const probeRank = ranks.get(key);
    if (probeRank) return probeRank;
    const seriesIndex = seriesOrder.indexOf(key);
    return [1, seriesIndex === -1 ? Number.MAX_SAFE_INTEGER : seriesIndex, 0, 0];
  };
  // Array.prototype.sort is stable, so keys that tie (an unrecognised channel
  // not in seriesMeta) keep the order they arrived in rather than shuffling.
  return [...metricKeys].sort((left, right) => {
    const a = rankFor(left);
    const b = rankFor(right);
    for (let index = 0; index < a.length; index += 1) {
      if (a[index] !== b[index]) return a[index] - b[index];
    }
    return 0;
  });
}

function markerMetricGroupLabel(key) {
  const probe = markerEngineProbeInfo(key);
  if (probe) return probe.group;
  const normalized = String(key ?? "").toLowerCase();
  if (normalized.includes("oilpressure")) return "Oil Pressure";
  if (normalized.includes("oiltemp")) return "Oil Temp";
  if (normalized.includes("fuelflow") || normalized.includes("fuelused")) return "Fuel";
  if (normalized.includes("rpm") || normalized.includes("map") || normalized.includes("power")) return "Power";
  if (isTurboMetricKey(normalized)) return "Turbo";
  if (normalized.includes("buss") || normalized.includes("amps") || normalized.includes("volts")) return "Electrical";
  if (normalized.includes("oat") || normalized.includes("ias") || normalized.includes("tas") || normalized.includes("groundspeed") || normalized.includes("altitude")) return "Air Data";
  return "Other";
}

function markerMetricSideLabel(label) {
  const match = String(label ?? "").match(/^([LRAF])\s+/);
  return match?.[1] ?? "";
}

function markerMetricItemLabel(key, metric = null) {
  const probe = markerEngineProbeInfo(key);
  if (probe) return probe.itemLabel;
  const label = markerMetricLabel(key, metric);
  const side = markerMetricSideLabel(label);
  const normalized = String(key ?? "").toLowerCase();
  if (normalized.includes("fuelflow")) return side ? `${side} Flow` : "Flow";
  if (normalized.includes("fuelused")) return side ? `${side} Used` : "Used";
  if (normalized.includes("rpm")) return side ? `${side} RPM` : "RPM";
  if (normalized === "map" || normalized.includes("map")) return side ? `${side} MAP` : "MAP";
  if (isTitMetricKey(normalized)) return side ? `${side} TIT` : "TIT";
  if (normalized.includes("upperdeck")) return side ? `${side} UDP` : "UDP";
  if (normalized.includes("fuelpressure")) return side ? `${side} Fuel P` : "Fuel P";
  if (normalized.includes("induction")) return side ? `${side} Ind T` : "Ind T";
  if (normalized.includes("compressor")) return side ? `${side} CDT` : "CDT";
  if (normalized.includes("oilpressure")) return side || "Oil";
  if (normalized.includes("oiltemp")) return side || "Oil";
  if (normalized.includes("buss") || normalized.includes("volts")) return side ? `${side} Bus` : "Bus";
  if (normalized.includes("amps")) return side ? `${side} Amps` : "Amps";
  return label;
}

function markerMetricText(key, point) {
  const metric = metricDisplayFor(key, point);
  const label = markerMetricItemLabel(key, metric);
  if (!metric) return `${label} n/a`;
  return `${label} ${Number(metric.value).toFixed(metric.digits)}${metric.suffix}`;
}

function markerMetricGroupsHtml(metricItems) {
  if (!metricItems.length) return '<span class="marker-detail-empty">No selected data at marker</span>';
  const groups = [];
  metricItems.forEach((item) => {
    let group = groups.find((candidate) => candidate.label === item.groupLabel);
    if (!group) {
      group = { label: item.groupLabel, items: [] };
      groups.push(group);
    }
    group.items.push(item);
  });
  return groups.map((group) => `
    <section class="marker-detail-group${group.items.length <= 2 ? " is-wide" : ""}">
      <h4>${escapeHtml(group.label)}</h4>
      <div>
        ${group.items.map((item) => item.html).join("")}
      </div>
    </section>
  `).join("");
}

function updateLiveMarkerDetailText(container, point) {
  if (!container || !point || !container.dataset.markerLiveSignature) return false;
  const metricKeys = [...getActiveSeries()];
  const expectedSignature = [
    container.id,
    getSelectedFlight()?.id ?? getSourceName(),
    metricKeys.join(","),
    getSelectedHealthKey() ?? "",
    getSelectedOperatingContextKey() ?? "",
    getSelectedCylinderNumber() ?? "",
    getDetectionSensitivity(),
  ].join("|");
  if (container.dataset.markerLiveSignature !== expectedSignature) return false;

  const setText = (selector, value) => {
    const element = container.querySelector(selector);
    if (!element || element.textContent === value) return;
    element.textContent = value;
  };
  setText("[data-marker-time]", formatMarkerPointTime(point));
  setText("[data-marker-elapsed]", `Elapsed ${formatElapsedTime(point.elapsedSeconds)}`);
  setText("[data-marker-phase]", point.phase ?? "Phase unknown");
  setText(
    "[data-marker-review]",
    "",
  );

  metricKeys.forEach((key) => {
    setText(`[data-marker-metric="${key}"]`, markerMetricText(key, point));
  });
  return true;
}

function updateMarkerDetail(container, point) {
  if (!container || !point) return;
  const metricKeys = [...getActiveSeries()];
  const liveSignature = [
    container.id,
    getSelectedFlight()?.id ?? getSourceName(),
    metricKeys.join(","),
    getSelectedHealthKey() ?? "",
    getSelectedOperatingContextKey() ?? "",
    getSelectedCylinderNumber() ?? "",
    getDetectionSensitivity(),
  ].join("|");
  const quickSignature = [
    container.id,
    getSelectedFlight()?.id ?? getSourceName(),
    markerPointId(point),
    point.elapsedSeconds,
    point.displayTime,
    point.utcDisplayTime,
    metricKeys.join(","),
    getSelectedHealthKey() ?? "",
    getSelectedOperatingContextKey() ?? "",
    getSelectedCylinderNumber() ?? "",
    getDetectionSensitivity(),
  ].join("|");
  if (container.dataset.markerSignature === quickSignature) return;
  const detection = selectedDetectionWindows().find((window) => pointInDetectionWindow(point, window)) ?? null;
  const detectionText = pointInDetectionWindow(point, detection)
    ? `<small>${escapeHtml(detection.label)}</small>`
    : "";
  const metrics = orderMarkerMetricKeys(metricKeys).map((key) => {
    const metric = metricDisplayFor(key, point);
    const color = seriesMeta[key]?.color ?? colors.text;
    const label = markerMetricItemLabel(key, metric);
    const groupLabel = markerMetricGroupLabel(key);
    if (!metric) {
      return {
        groupLabel,
        html: `<span data-marker-metric="${escapeHtml(key)}" style="color:${color}; opacity:0.62">${escapeHtml(label)} n/a</span>`,
      };
    }
    return {
      groupLabel,
      html: `<span data-marker-metric="${escapeHtml(key)}" style="color:${color}">${escapeHtml(label)} ${Number(metric.value).toFixed(metric.digits)}${metric.suffix}</span>`,
    };
  }).filter(Boolean);
  const html = `
    <div class="marker-detail-title">
      <strong data-marker-time>${formatMarkerPointTime(point)}</strong>
      <small data-marker-elapsed>Elapsed ${formatElapsedTime(point.elapsedSeconds)}</small>
      <small data-marker-phase>${escapeHtml(point.phase ?? "Phase unknown")}</small>
      ${detectionText}
      <small data-marker-review></small>
    </div>
    <div class="marker-detail-grid">
      ${markerMetricGroupsHtml(metrics)}
    </div>
  `;
  container.dataset.markerSignature = quickSignature;
  container.dataset.markerLiveSignature = liveSignature;
  container.innerHTML = html;
}

  return {
    updateScrubReadout,
  };
}
